import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBasket, X } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { useUserStore } from '@/store/userStore';

/**
 * Вечерняя строка на главной: «не добрано — собрать во ВкусВилле?» (05.10.2026).
 *
 * Зачем отдельный вход. Корзина живёт в чате, а чатом за месяц пользовались
 * пять человек — встроенную туда фишку почти никто бы не нашёл. По базе при этом
 * ВкусВилл называют в записях 9 человек, остальные сети по одному.
 *
 * Правила ненавязчивости — те же, что у строки Дофи:
 *  - только тем, у кого ВкусВилл есть (profiles.vkusvill === true): магазинов нет
 *    в большинстве городов. Кто не отвечал — тому на главной ничего не показываем,
 *    вопрос один раз задаёт Дофи в чате;
 *  - только вечером и только когда есть что добирать: человек уже записал еду
 *    сегодня, а до нормы осталось заметно (иначе это реклама, а не помощь);
 *  - крестик прячет до завтра, без вопросов;
 *  - стоит ПОД кнопками записи, а не рядом со строкой Дофи: два совета про белок
 *    друг над другом — ровно те «три голоса», от которых уходили.
 */

const KEY = 'vv_nudge_hidden';
const EVENING_FROM = 16;
const MIN_PROTEIN_GAP = 20;
const MIN_KCAL_GAP = 300;

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function VkusvillNudge({
  totals,
  targets,
}: {
  totals: { calories: number; protein: number };
  targets: { calories: number; protein: number };
}) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile } = useUserStore();
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(KEY) === today(); } catch { return false; }
  });

  const proteinGap = Math.round(targets.protein - totals.protein);
  const kcalGap = Math.round(targets.calories - totals.calories);
  const worthIt = proteinGap >= MIN_PROTEIN_GAP || kcalGap >= MIN_KCAL_GAP;

  if (profile?.vkusvill !== true) return null;
  if (hidden || totals.calories <= 0 || kcalGap < 120 || !worthIt) return null;
  if (new Date().getHours() < EVENING_FROM) return null;

  function open() {
    haptic('light');
    // Чат подхватит флаг и сразу попросит Дофи собрать корзину — без набора текста.
    try { sessionStorage.setItem('dofi_action', 'vv_basket'); } catch {}
    nav('/chat');
  }

  function hide(e: React.MouseEvent) {
    e.stopPropagation();
    try { localStorage.setItem(KEY, today()); } catch {}
    setHidden(true);
  }

  const what = proteinGap >= MIN_PROTEIN_GAP
    ? `Не хватает ${proteinGap} г белка`
    : `До нормы ещё ${kcalGap} ккал`;

  return (
    <div
      role="button"
      onClick={open}
      className="mb-4 flex items-center gap-3 rounded-2xl border border-line bg-bg-card px-3 py-2.5 active:scale-[0.99] transition-transform cursor-pointer"
    >
      <div className="w-9 h-9 rounded-xl bg-emerald-glow/10 text-emerald-glow flex items-center justify-center shrink-0">
        <ShoppingBasket size={17} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink leading-snug">{what} — собрать во ВкусВилле?</div>
        <div className="text-[11px] text-ink-dim">Дофи подберёт продукты под остаток и соберёт корзину</div>
      </div>
      <button
        type="button"
        onClick={hide}
        aria-label="Скрыть до завтра"
        className="w-7 h-7 rounded-full text-ink-dim flex items-center justify-center shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
}
